import React, { useRef, useState } from "react";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import ClaudeIcon from "@/assets/images/claude.svg?react";
import CodexIcon from "@/assets/images/codex.svg?react";
import CursorIcon from "@/assets/images/cursor.svg?react";
import GeminiIcon from "@/assets/images/gemini.svg?react";
import VscodeIcon from "@/assets/images/vscode.svg?react";
import { Button } from "@/components/common/Button";
import { Fold } from "@/components/common/Fold";
import { TooltipButton } from "@/components/common/TooltipButton";
import { CodeBlock } from "@/components/common/CodeBlock";
import { SectionRule } from "@/components/common/SectionRule";
import { showNotificationGlobal } from "@/stores/useNotificationStore";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import {
  claudeCodeCmd,
  claudeDesktopEntry,
  codexCmd,
  cursorDeepLink,
  geminiCmd,
  isLocalEnv,
  mcpServerEntry,
  mcpServerUrl,
  vscodeDeepLink,
  vscodeServerEntry,
} from "./mcpConnect";

const LOGO = { width: 18, height: 18, "aria-hidden": true } as const;

interface Snippet {
  code: string;
  label: string;
}
interface Step {
  text?: React.ReactNode;
  snippet?: Snippet;
  button?: { label: string; link: string };
}
interface HowTo {
  steps: Step[];
  note?: string;
  manual?: Snippet;
}
interface Client extends HowTo {
  name: string;
  sub: string;
  logo: React.ReactNode;
  claudeCode?: Snippet;
  /// Local goes through the `mcp-remote` bridge, which no one-click install or
  /// hosted connector can reach, so these steps paste config instead.
  local?: HowTo;
}

const APPROVE: Step = { text: "Approve access in the browser." };
const B: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <b className="font-semibold">{children}</b>
);

const clients = (orgId: string): Client[] => {
  const url = mcpServerUrl(orgId);
  const link: Step = {
    text: "Copy the link.",
    snippet: { code: url, label: "Server URL" },
  };
  const entry: Snippet = {
    code: mcpServerEntry(orgId),
    label: "JSON: mcpServers entry for MyCorr",
  };
  const vsEntry: Snippet = {
    code: vscodeServerEntry(orgId),
    label: "JSON: VS Code servers entry for MyCorr",
  };
  return [
    {
      name: "Claude",
      sub: "Desktop, web, Code",
      logo: <ClaudeIcon {...LOGO} />,
      steps: [
        link,
        {
          text: (
            <>
              In Claude, go to{" "}
              <B>Settings → Connectors → Add custom connector</B> and paste it.
            </>
          ),
        },
        APPROVE,
      ],
      claudeCode: {
        code: claudeCodeCmd(orgId),
        label: "Shell: claude mcp add for MyCorr",
      },
      local: {
        steps: [
          {
            text: (
              <>
                In Claude Desktop, go to{" "}
                <B>Settings → Developer → Edit Config</B>, paste this inside{" "}
                <code>mcpServers</code>, then relaunch.
              </>
            ),
            snippet: {
              code: claudeDesktopEntry(orgId),
              label: "JSON: Claude Desktop mcpServers entry for MyCorr",
            },
          },
          APPROVE,
        ],
      },
    },
    {
      name: "ChatGPT",
      sub: "Paste a link",
      logo: <CodexIcon {...LOGO} />,
      steps: [
        link,
        {
          text: (
            <>
              In ChatGPT, turn on <B>Developer mode</B>, then <B>Create</B> a
              connector and paste it.
            </>
          ),
        },
        APPROVE,
      ],
      local: { steps: [], note: "ChatGPT can't reach localhost." },
    },
    {
      name: "Cursor",
      sub: "One-click install",
      logo: <CursorIcon {...LOGO} />,
      steps: [
        {
          button: {
            label: "Add to Cursor",
            link: cursorDeepLink(orgId),
          },
        },
        APPROVE,
      ],
      manual: entry,
      local: {
        steps: [
          {
            text: (
              <>
                Paste this inside <code>mcpServers</code> in Cursor's MCP
                settings.
              </>
            ),
            snippet: entry,
          },
          APPROVE,
        ],
      },
    },
    {
      name: "VS Code",
      sub: "GitHub Copilot",
      logo: <VscodeIcon {...LOGO} />,
      steps: [
        {
          button: {
            label: "Install in VS Code",
            link: vscodeDeepLink(orgId),
          },
        },
        APPROVE,
      ],
      manual: vsEntry,
      local: {
        steps: [
          {
            text: (
              <>
                Paste this inside <code>servers</code> in{" "}
                <code>.vscode/mcp.json</code>.
              </>
            ),
            snippet: vsEntry,
          },
          APPROVE,
        ],
      },
    },
    {
      name: "Codex",
      sub: "Terminal command",
      logo: <CodexIcon {...LOGO} />,
      steps: [
        {
          text: "Run in your terminal.",
          snippet: {
            code: codexCmd(orgId),
            label: "Shell: codex mcp add for MyCorr",
          },
        },
        APPROVE,
      ],
    },
    {
      name: "Gemini CLI",
      sub: "Terminal command",
      logo: <GeminiIcon {...LOGO} />,
      steps: [
        {
          text: "Run in your terminal.",
          snippet: {
            code: geminiCmd(orgId),
            label: "Shell: gemini mcp add for MyCorr",
          },
        },
        APPROVE,
      ],
    },
    {
      name: "Other",
      sub: "Any MCP client",
      logo: <MoreHorizIcon sx={{ fontSize: 18 }} />,
      steps: [link, { text: "Add it as a remote MCP server." }, APPROVE],
      manual: entry,
      local: {
        steps: [
          {
            text: (
              <>
                Paste this inside your client's <code>mcpServers</code>.
              </>
            ),
            snippet: entry,
          },
          APPROVE,
        ],
      },
    },
  ];
};

function launchDeepLink(url: string, appLabel: string) {
  let opened = false;
  const markOpened = () => {
    opened = true;
  };
  window.addEventListener("blur", markOpened, { once: true });

  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  iframe.src = url;
  document.body.appendChild(iframe);

  window.setTimeout(() => {
    iframe.remove();
    window.removeEventListener("blur", markOpened);
    if (!opened && !document.hidden) {
      showNotificationGlobal(
        `If ${appLabel} didn't open, make sure it's installed — or pick "Other" to paste the server URL.`,
        "default",
      );
    }
  }, 1500);
}

const codeBox = ({ code, label }: Snippet) => (
  <CodeBlock
    code={code}
    ariaLabel={label}
    onCopy={() => showNotificationGlobal("Copied", "success")}
    className="bg-bg! border-border select-text"
  />
);

export const AccessConnectPanel: React.FC = () => {
  const [open, setOpen] = useState<string | null>(null);
  // The panel keeps showing the last client while it folds shut.
  const [shown, setShown] = useState<string | null>(null);
  if (open && open !== shown) setShown(open);
  const [manualOpen, setManualOpen] = useState(false);
  const cards = useRef<Record<string, HTMLButtonElement | null>>({});
  // Connections are org-gated: every snippet advertises the ACTIVE org's
  // alias URL, matching the context the rest of the app is showing.
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  // Null only while the org store hydrates: a logged-in account always has
  // an org (D36's lazy personal-org mint at login), so this is a loading
  // state, not a reachable no-org UI.
  if (!activeOrgId) return null;

  const list = clients(activeOrgId);
  const client = list.find((c) => c.name === shown);
  const howTo = isLocalEnv() && client?.local ? client.local : client;
  const close = () => {
    if (open) cards.current[open]?.focus();
    setOpen(null);
  };

  return (
    <section>
      <SectionRule
        inset
        icon={<AutoAwesomeOutlinedIcon sx={{ fontSize: 14 }} />}
        label="AI clients"
      />
      <div>
        <div
          className="flex gap-2 pb-2 overflow-x-auto scrollbar-overlay-hover"
          role="group"
          aria-label="AI clients"
        >
          {list.map((c) => {
            const expanded = open === c.name;
            return (
              <button
                key={c.name}
                type="button"
                ref={(el) => {
                  cards.current[c.name] = el;
                }}
                aria-expanded={expanded}
                aria-controls="ai-client-howto"
                className={`flex flex-col shrink-0 w-30 items-center justify-center gap-1 h-22 p-2 border rounded-app cursor-pointer transition-colors ${
                  expanded
                    ? "border-accent bg-bg-hover"
                    : "border-border bg-bg-subtle hover:border-accent focus-visible:border-accent"
                }`}
                style={{ transitionDuration: "var(--duration-micro)" }}
                onClick={() => setOpen(expanded ? null : c.name)}
              >
                <span className="grid place-items-center w-5 h-5 text-fg">
                  {c.logo}
                </span>
                <span className="text-fg-medium">{c.name}</span>
                <span
                  className="text-fg-muted whitespace-nowrap leading-tight"
                  style={{ fontSize: "var(--font-size-x-small)" }}
                >
                  {c.sub}
                </span>
              </button>
            );
          })}
        </div>

        <Fold open={open !== null}>
          {client && howTo && (
            <div className="pt-1">
              <div
                id="ai-client-howto"
                className="flex flex-col gap-3 border border-border rounded-app bg-bg-subtle p-3"
              >
                <div className="flex items-center gap-2 text-fg-semibold">
                  <span className="grid place-items-center w-4.5 h-4.5">
                    {client.logo}
                  </span>
                  {client.name}
                  <span className="ml-auto">
                    <TooltipButton
                      title="Close"
                      aria-label="Close"
                      variant="default"
                      size="sm"
                      onClick={close}
                    >
                      <CloseIcon />
                    </TooltipButton>
                  </span>
                </div>
                {howTo.note && (
                  <p className="text-fg-muted-regular m-0">{howTo.note}</p>
                )}
                {howTo.steps.length > 0 && (
                  <ol className="flex flex-col gap-3 list-none m-0 p-0">
                    {howTo.steps.map((step, i) => (
                      <li
                        key={i}
                        className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-2 items-start"
                      >
                        <span className="grid place-items-center w-6 h-6 rounded-full bg-bg-hover text-mono-small font-medium">
                          {i + 1}
                        </span>
                        <div className="flex flex-col gap-2 pt-1 min-w-0">
                          {step.text && <span>{step.text}</span>}
                          {step.snippet && codeBox(step.snippet)}
                          {step.button && (
                            <Button
                              variant="primary"
                              size="sm"
                              className="self-start"
                              onClick={() =>
                                launchDeepLink(step.button!.link, client.name)
                              }
                            >
                              {step.button.label}
                            </Button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
                {howTo.manual && (
                  <div>
                    <Button
                      variant="quiet-link"
                      size="xs"
                      aria-expanded={manualOpen}
                      onClick={() => setManualOpen((v) => !v)}
                    >
                      <ChevronRightIcon
                        className="icon-sm"
                        style={{
                          rotate: manualOpen ? "90deg" : "0deg",
                          transition:
                            "rotate var(--duration-short) var(--ease-move)",
                        }}
                      />
                      Manual setup
                    </Button>
                    <Fold open={manualOpen}>
                      <div className="pt-2">{codeBox(howTo.manual)}</div>
                    </Fold>
                  </div>
                )}
                {client.claudeCode && (
                  <div className="flex flex-col gap-2 pt-3 border-t border-border">
                    <span className="text-fg-semibold">Claude Code</span>
                    {codeBox(client.claudeCode)}
                  </div>
                )}
              </div>
            </div>
          )}
        </Fold>
      </div>
    </section>
  );
};
